import React, { useMemo } from "react"

export default function QuizResultDetail({ quiz, answers, meta, showCorrectness = false }) {
  const mapAnswers = useMemo(() => (answers && typeof answers === 'object') ? answers : {}, [answers])
  const getQid = (q, idx) => (q && q.id ? String(q.id) : String(idx))
  const isMulti = (q) => Boolean(q?.multiSelect || (Array.isArray(q?.correctChoiceIds) && q.correctChoiceIds.length > 1))

  const isSelected = (q, idx, choiceId) => {
    const a = mapAnswers[getQid(q, idx)]
    let result = Array.isArray(a) ? a.includes(choiceId) : a === choiceId;
    return result;
  }

  const isCorrectChoice = (q, choiceId) => {
    if (!showCorrectness) return false
    const right = Array.isArray(q?.correctChoiceIds) ? q.correctChoiceIds : []
    return right.includes(choiceId)
  }

  return (
    <section id="quiz-result-print" className="tw:bg-white tw:border tw:border-gray-100 tw:rounded-xl tw:shadow-xs">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #quiz-result-print, #quiz-result-print * { visibility: visible; }
          #quiz-result-print { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .tw-bg-green-50 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .tw-bg-red-50 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .tw-bg-gray-50 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .tw-bg-green-100 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .tw-bg-red-100 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .tw-bg-gray-100 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <div className="tw:flex tw:items-center tw:justify-between tw:bg-gray-50 tw:px-6 tw:py-4">
        <div>
          <h3 className="tw:text-lg tw:text-gray-800">Dettaglio Risultato</h3>
          <div className="tw:text-sm tw:text-gray-600">
            <span>Utente: {meta?.firstname} {meta?.lastname} ({meta?.user_email || meta?.user_id})</span>
            <span className="tw:ml-3">Data: {meta?.completed_at || meta?.created_at || '—'}</span>
            <span className="tw:ml-3">Durata: {meta?.duration_sec != null ? `${Math.floor(meta.duration_sec/60)}:${String(meta.duration_sec%60).padStart(2,'0')}` : '—'}</span>
            <span className="tw:ml-3">Punteggio: {meta?.percent != null ? `${Math.round(meta.percent)}% (${meta?.correct_count}/${meta?.total_questions})` : '—'}</span>
          </div>
        </div>
        <div className="no-print">
          <button className="tw:px-4 tw:py-2 tw:bg-blue-600 tw:text-white tw:rounded" onClick={() => window.print()}>Stampa</button>
        </div>
      </div>
      <div className="tw:p-6 tw:space-y-4">
        <h4 className="tw:text-base tw:font-medium">{quiz?.title}</h4>
        {(quiz?.questions || []).map((q, idx) => (
          <div key={getQid(q, idx)} className="tw:border tw:border-gray-200 tw:rounded-sm tw:p-4">
            <div className="tw:font-medium tw:mb-3">{idx + 1}. {q.text}</div>
            {mapAnswers[getQid(q, idx)] == null && (
              <div className="tw:mb-2 tw:text-xs tw:bg-yellow-50 tw:text-yellow-800 tw:border tw:border-yellow-200 tw:rounded-sm tw:px-2 tw:py-1">
                Nessuna risposta registrata — domanda aggiunta successivamente
              </div>
            )}
            <div className="tw:space-y-2">
              {(q.choices || []).map((c) => {
                const selected = isSelected(q, idx, c.id)
                 const correct = isCorrectChoice(q, c.id)
                 const correctnessAvailable = showCorrectness && Array.isArray(q?.correctChoiceIds)
                 const bg = correctnessAvailable && correct ? 'tw:bg-green-50' : (correctnessAvailable && selected ? 'tw:bg-red-50' : 'tw:bg-gray-50')
                 const text = correctnessAvailable && correct ? 'tw:text-green-800' : (correctnessAvailable && selected ? 'tw:text-red-800' : 'tw:text-gray-800')
                 const border = correctnessAvailable && correct ? 'tw:border-green-200' : (correctnessAvailable && selected ? 'tw:border-red-200' : 'tw:border-gray-200')
                return (
                  <div key={c.id} className={`tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:border ${border} ${bg} tw:rounded-sm`}>
                    <span className={`tw:inline-block tw:text-xs tw-font-mono tw:px-2 tw:py-1 tw-rounded ${correct ? 'tw:bg-green-100 tw:text-green-700' : selected ? 'tw:bg-red-100 tw:text-red-700' : 'tw:bg-gray-100 tw:text-gray-700'}`}>
                       {correctnessAvailable
                         ? (isMulti(q) ? (selected ? (correct ? '✓' : '✗') : (correct ? '✓' : '•')) : (correct ? '✓' : (selected ? '✗' : '•')))
                         : (selected ? '•' : '○')}
                    </span>
                    <span className={`${text}`}>{c.text}</span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        {(() => {
          const keys = Object.keys(mapAnswers || {});
          const knownIds = new Set((quiz?.questions || []).map((q, idx) => (q && q.id ? String(q.id) : String(idx))));
          const extras = keys.filter((k) => !knownIds.has(String(k)));
          if (extras.length === 0) return null;
          return (
            <div className="tw:border tw:border-amber-200 tw:bg-amber-50 tw:text-amber-900 tw:rounded-sm tw:p-4">
              <div className="tw:font-medium tw:mb-2">Risposte per domande non più presenti</div>
              <div className="tw:space-y-2">
                {extras.map((qid) => {
                  const val = mapAnswers[qid];
                  const list = Array.isArray(val) ? val : [val];
                  return (
                    <div key={qid} className="tw:flex tw:items-center tw:gap-2">
                      <span className="tw:text-xs tw-font-mono tw:px-2 tw:py-1 tw:bg-amber-100 tw:text-amber-800 tw:rounded-sm">{qid}</span>
                      <span className="tw:text-xs">scelte: {list.filter(Boolean).join(', ') || '—'}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}
      </div>
    </section>
  )
}
